import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import type { LookupSelectionMode, LookupUser } from './lookupTypes';
import { formatUserDisplayName } from './lookupUtils';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const EMPTY_USERS: LookupUser[] = [];

interface UserLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: LookupSelectionMode;
  title?: string;
  initialSelected?: LookupUser[];
  onConfirm: (selected: LookupUser[]) => void;
}

function mapApiUser(raw: any): LookupUser {
  return {
    id: raw.id,
    firstName: raw.firstName || '',
    lastName: raw.lastName || '',
    email: raw.email,
    title: raw.title,
  };
}

const UserLookupModal: React.FC<UserLookupModalProps> = ({
  isOpen,
  onClose,
  mode,
  title = 'Users',
  initialSelected = EMPTY_USERS,
  onConfirm,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<LookupUser[]>(initialSelected);

  // Reset only when the modal opens — do not depend on initialSelected identity,
  // since a default [] is a new reference every render and would clear typed search.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(initialSelected);
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally open-only
  }, [isOpen]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lookup-users', page, pageSize, appliedSearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        active: 'true',
      });
      if (appliedSearch) params.append('search', appliedSearch);
      const response = await api.get(`/users?${params}`);
      return response.data;
    },
    enabled: isOpen,
    staleTime: 15000,
  });

  const users: LookupUser[] = useMemo(
    () => (data?.users || []).map(mapApiUser),
    [data?.users]
  );

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const isSelected = useCallback(
    (id: string) => selected.some((u) => u.id === id),
    [selected]
  );

  const toggleUser = (user: LookupUser) => {
    if (mode === 'single') {
      setSelected([user]);
      return;
    }
    setSelected((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  const handleSelectAllPage = (checked: boolean) => {
    if (mode === 'single') return;
    if (checked) {
      const merged = [...selected];
      users.forEach((user) => {
        if (!merged.some((u) => u.id === user.id)) merged.push(user);
      });
      setSelected(merged);
    } else {
      const pageIds = new Set(users.map((u) => u.id));
      setSelected((prev) => prev.filter((u) => !pageIds.has(u.id)));
    }
  };

  const allPageSelected =
    mode === 'multi' && users.length > 0 && users.every((u) => isSelected(u.id));
  const somePageSelected =
    mode === 'multi' && users.some((u) => isSelected(u.id)) && !allPageSelected;

  const handleConfirm = () => {
    onConfirm(mode === 'single' ? selected.slice(0, 1) : selected);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">{title}</Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-4 py-3 border-b border-gray-100">
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search users..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Search
                    </button>
                  </form>
                  {mode === 'multi' && selected.length > 0 && (
                    <p className="mt-2 text-xs text-indigo-700">{selected.length} user(s) selected</p>
                  )}
                </div>

                <div className="flex-1 overflow-auto min-h-[240px]">
                  {isLoading ? (
                    <div className="p-8 text-center text-gray-500 text-sm">Loading users...</div>
                  ) : users.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">No users found</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 w-10">
                            {mode === 'multi' && (
                              <input
                                type="checkbox"
                                checked={allPageSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = somePageSelected;
                                }}
                                onChange={(e) => handleSelectAllPage(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                aria-label="Select all on page"
                              />
                            )}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {users.map((user) => {
                          const checked = isSelected(user.id);
                          return (
                            <tr
                              key={user.id}
                              className={`cursor-pointer hover:bg-gray-50 ${checked ? 'bg-indigo-50/50' : ''}`}
                              onClick={() => toggleUser(user)}
                            >
                              <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type={mode === 'single' ? 'radio' : 'checkbox'}
                                  name="user-lookup"
                                  checked={checked}
                                  onChange={() => toggleUser(user)}
                                  className="h-4 w-4 text-indigo-600 border-gray-300"
                                  aria-label={`Select ${formatUserDisplayName(user)}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatUserDisplayName(user)}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{user.email || '—'}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{user.title || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1"
                    >
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <span>
                      {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                    </span>
                    {isFetching && !isLoading && <span className="text-xs text-gray-400">Updating...</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-2 text-gray-600">Page {page} of {totalPages}</span>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={selected.length === 0}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {mode === 'single' ? 'Select' : `Add (${selected.length})`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserLookupModal;
